import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AdminWinners.css';

// Medal icons for tiers
const getMedalIcon = (tier) => {
  switch (tier) {
    case 1:
      return <span className="medal gold-medal" title="Tier 1 - 1 Point">&#129351;</span>;
    case 2:
      return <span className="medal silver-medal" title="Tier 2 - 2 Points">&#129352;</span>;
    case 3:
      return <span className="medal bronze-medal" title="Tier 3 - 3 Points">&#129353;</span>;
    default:
      return null;
  }
};

function AdminWinners() {
  const [categories, setCategories] = useState([]);
  const [winners, setWinners] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [overallWinner, setOverallWinner] = useState(null);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [nomineesExpanded, setNomineesExpanded] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [categoriesData, winnersData] = await Promise.all([
        api.getAdminCategories(),
        api.getWinners()
      ]);

      setCategories(categoriesData);
      setWinners(winnersData);

      // Select first category by default
      if (categoriesData.length > 0 && !selectedCategory) {
        setSelectedCategory(categoriesData[0]);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetWinner = async (winner) => {
    if (!selectedCategory) return;

    const currentWinner = winners[selectedCategory.name];

    // Toggle: if clicking the current winner, delete it
    if (currentWinner?.winner === winner) {
      setSaving(true);
      setError('');

      try {
        await api.deleteWinner(selectedCategory.name);
        setWinners(prev => {
          const updated = { ...prev };
          delete updated[selectedCategory.name];
          return updated;
        });
      } catch (err) {
        setError(err.message || 'Failed to remove winner');
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    setError('');

    try {
      const result = await api.setWinner(selectedCategory.name, winner);

      setWinners(prev => ({
        ...prev,
        [selectedCategory.name]: {
          winner: result.winner.winner,
          tier: result.winner.tier,
          setAt: result.winner.setAt
        }
      }));

      if (result.allWinnersSet && result.overallWinner) {
        setOverallWinner(result.overallWinner);
        setShowWinnerModal(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to set winner');
    } finally {
      setSaving(false);
    }
  };

  const handleCalculateScores = async () => {
    setSaving(true);
    setError('');

    try {
      const result = await api.calculateScores();
      if (result.overallWinner) {
        setOverallWinner(result.overallWinner);
        setShowWinnerModal(true);
      }
      alert(`Scores calculated! ${result.winnersSet}/${result.totalCategories} winners set.`);
    } catch (err) {
      setError(err.message || 'Failed to calculate scores');
    } finally {
      setSaving(false);
    }
  };

  const winnersCount = Object.keys(winners).length;
  const currentWinner = selectedCategory ? winners[selectedCategory.name] : null;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Set Winners</h1>
        <p>{winnersCount}/{categories.length} winners set</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Progress Bar */}
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${(winnersCount / categories.length) * 100}%` }}
        ></div>
      </div>

      {/* Quick Navigation - Moved to top */}
      <div className="quick-nav">
        <div className="quick-nav-header">
          <h3>Categories</h3>
          <button
            className="btn btn-secondary btn-small"
            onClick={handleCalculateScores}
            disabled={saving || winnersCount === 0}
          >
            Recalculate Scores
          </button>
        </div>
        <div className="category-chips">
          {categories.map(category => (
            <button
              key={category._id}
              className={`category-chip ${category._id === selectedCategory?._id ? 'active' : ''} ${winners[category.name] ? 'completed' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {getMedalIcon(category.tier)}
              <span className="chip-name">
                {category.name.length > 15 ? category.name.substring(0, 13) + '...' : category.name}
              </span>
              {winners[category.name] && <span className="chip-check">&#10003;</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Category Info */}
      {selectedCategory && (
        <div className="category-info">
          <div className="category-title">
            {getMedalIcon(selectedCategory.tier)}
            <h2>{selectedCategory.name}</h2>
          </div>
          <span className={`tier-badge tier-${selectedCategory.tier}`}>
            Tier {selectedCategory.tier} - {selectedCategory.tier === 1 ? '1 Point' : selectedCategory.tier === 2 ? '2 Points' : '3 Points'}
          </span>
          {currentWinner && (
            <div className="current-winner">
              Winner: <strong>{currentWinner.winner}</strong>
            </div>
          )}
        </div>
      )}

      {/* Nominees Accordion */}
      <div className="nominees-accordion">
        <button
          className="accordion-header"
          onClick={() => setNomineesExpanded(!nomineesExpanded)}
        >
          <span>Select Winner ({selectedCategory?.nominees?.length || 0} nominees)</span>
          <span className={`accordion-icon ${nomineesExpanded ? 'expanded' : ''}`}>&#9660;</span>
        </button>

        {nomineesExpanded && (
          <div className="accordion-content">
            {!selectedCategory?.nominees?.length ? (
              <p className="no-nominees">No nominees found for this category</p>
            ) : (
              <div className="nominees-grid">
                {selectedCategory.nominees.map(nominee => (
                  <button
                    key={nominee.nominee}
                    className={`nominee-card ${currentWinner?.winner === nominee.nominee ? 'selected' : ''}`}
                    onClick={() => handleSetWinner(nominee.nominee)}
                    disabled={saving}
                  >
                    <div className="nominee-info">
                      <span className="nominee-name">{nominee.nominee}</span>
                      {nominee.movie !== nominee.nominee && (
                        <span className="nominee-movie">{nominee.movie}</span>
                      )}
                    </div>
                    {currentWinner?.winner === nominee.nominee && (
                      <span className="winner-badge">WINNER</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Overall Winner Modal */}
      {showWinnerModal && overallWinner && (
        <div className="modal-overlay" onClick={() => setShowWinnerModal(false)}>
          <div className="modal winner-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trophy-icon">&#127942;</div>
            <h2>All Winners Set!</h2>
            <p>The overall winner is:</p>
            <div className="winner-name">{overallWinner.name}</div>
            <div className="winner-score">{overallWinner.score} points</div>
            <button
              className="btn btn-primary"
              onClick={() => setShowWinnerModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminWinners;
